import { useEffect, useMemo, useRef, useState } from "react";

import { DateTime } from "luxon";

import adaptivePlugin from "@fullcalendar/adaptive";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { Draggable, EventReceiveArg } from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useLeads from "../../../data/useLeads";
import useLeadsWithFilters from "../../../data/useLeadsWithFilters";
import useVehiclesMutations from "../../../data/useVehiclesMutations";
import { IVehicle } from "../../../models/Vehicle";
import LoadingWheel from "../../shared/LoadingWheel";
import { ILead } from './../../../models/Lead';
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import classNames from "../../../utils/classNames";
import { getLeadPostcode } from "../../../utils/getLeadLocation";

interface DropOffCalendarProps {
  currentLead?: ILead;
}

export default function DropOffCalendar({ currentLead }: DropOffCalendarProps) {
  const calendarRef = useRef<FullCalendar | null>(null);
  const calendarWrapperRef = useRef<HTMLDivElement | null>(null);
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const { update: updateLead } = useLeads(false, "DropOffCalendar");
  const { leadsWithFilters } = useLeadsWithFilters({
    query: "",
    pageFilter: 4,
  }, "DropOffCalendar");
  const { update: updateVehicle } = useVehiclesMutations();
  const [isSubmitting, setIsSubmitting] = useState(false);


  const scheduled = useMemo(
    () =>
      leadsWithFilters.data?.data?.leads.filter((l) => l.vehicle?.scheduledCollectionOn !== null),
    [leadsWithFilters.data]);

  const unscheduled = useMemo(() => {
    var leads = leadsWithFilters.data?.data?.leads.filter((l) => l.vehicle?.scheduledCollectionOn === null);

    if (currentLead) {
      leads = leads?.filter(x => x.id === currentLead.id);
    }

    return leads;
  }, [leadsWithFilters.data, currentLead]);

  const handleEventChange = (e: EventReceiveArg) => {
    const event = e.event;
    let eventEnd = event.end;
    let eventStart = event.start;

    const lead = leadsWithFilters.data?.data.leads.find(
      (l) => l.id === event.id
    );

    if (!lead) {
      //TODO: Notif here to say failed
      return;
    }

    setIsSubmitting(true);

    if (!eventEnd) {
      let end = DateTime.fromJSDate(eventStart!);
      end = end.plus({ hours: 1 });
      eventEnd = end.toJSDate();
    }

    const eventDateTime = eventStart?.toISOString();

    const vehicle: IVehicle = {
      ...lead?.vehicle!,
      scheduledCollectionOn: eventDateTime ?? null,
    };

    var updatedLead = {
      ...lead,
      vehicle: vehicle,
    };

    updateVehicle.mutate(vehicle, {
      ...saveHandlers,
      // onSuccess: () => {
      //   // updateLead.mutate(updatedLead, {
      // },
      onSettled: () => setIsSubmitting(false),

    });
  };

  const handleMarkedAsCollected = () => {

    if (!currentLead) {
      //TODO: Notif here to say failed
      return;
    }

    const vehicle: IVehicle = {
      ...currentLead?.vehicle!,
      collectedOn: DateTime.now().toISO()
    };

    updateVehicle.mutate(vehicle, {
      ...saveHandlers,
      // onSuccess: () => {
      //   // updateLead.mutate(updatedLead, {
      // },
      onSettled: () => setIsSubmitting(false),

    });
  };

  const handleDroppedLead = (lead: ILead) => {
    // console.log("dropped lead", lead)

    if (lead === undefined) {
      saveHandlers.onError();
      return;
    }

    const vehicle: IVehicle = {
      ...lead?.vehicle!,
      scheduledCollectionOn: null,
      collectedOn: null,
    };

    updateVehicle.mutate(vehicle, saveHandlers);
  };

  useEffect(() => {
    var containerEl = document.getElementById("job-events");
    if (containerEl) {
      new Draggable(containerEl!, {
        itemSelector: ".fc-event",
        eventData: (eventEl) => {
          return {
            title: eventEl.innerText,
            id: eventEl.id,
          };
        },
      });
    }
  }, [leadsWithFilters.isLoading]);

  if (leadsWithFilters.isLoading) {
    return <LoadingWheel />;
  } else {
    return (
      <>
        <div className="mb-6 flex justify-between">
          <h1 className="text-xl">Book Drop Off {currentLead && `For ${currentLead.vehicle?.vrm}`}</h1>
          {currentLead && currentLead.vehicle?.scheduledCollectionOn && !currentLead.vehicle?.collectedOn && (
            <ButtonInput label={"Mark As Collected"} onClick={() => handleMarkedAsCollected()} classes={undefined} />
          )}
        </div>

        {updateVehicle.isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-50 flex justify-center items-center z-10">
            <LoadingWheel />
          </div>
        )}
        <div
          id="job-events"
          className={classNames("w-full grid lg:grid-cols-5 grid-cols-3 gap-2 mb-6 min-h-[40px]")}
        >
          {unscheduled!.map((lead) => (
            <div
              className="fc-event col-span-1 text-sm flex flex-row items-center space-x-2 bg-red-200 p-1 rounded-md cursor-pointer"
              title={lead.vehicle?.vrm}
              id={lead.id}
              key={lead.id}
            >
              <span>
                {lead.vehicle?.hasAlert && "⚠️"}
                {lead.vehicle?.isVan && "🚚"}
              </span>
              <span>
                {lead.vehicle?.vrm
                  ? `${lead.vehicle.vrm}, ${getLeadPostcode(lead)} `
                  : "No VRM"}
              </span>
            </div>
          ))}
        </div>

        <div ref={calendarWrapperRef}>
          <FullCalendar
            ref={calendarRef}
            allDaySlot={false}
            schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
            viewClassNames={"timeline"}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              adaptivePlugin,
            ]}
            initialView="timeGridWeek"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "",
            }}
            titleFormat={{
              month: "long",
              year: "numeric",
              day: "numeric",
              weekday: "long",
            }}
            editable={true}
            defaultTimedEventDuration="01:00:00"
            eventDurationEditable={true}
            eventStartEditable={true}
            nowIndicator={true}
            contentHeight="auto"
            slotMinTime="06:00:00"
            slotMaxTime="20:00:00"
            events={scheduled?.map((t) => ({
              id: t.id,
              title: t.vehicle?.vrm,
              start: t.vehicle?.scheduledCollectionOn!,
            }))}
            eventReceive={handleEventChange}
            eventDragStop={(info) => {
              const el = calendarWrapperRef.current;
              if (!el) return;

              const rect = el.getBoundingClientRect();
              const x = info.jsEvent.clientX;
              const y = info.jsEvent.clientY;

              const outside =
                x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;

              if (outside) {
                const lead = leadsWithFilters.data?.data.leads.find(
                  (l) => l.id === info.event.id
                );
                if (lead) handleDroppedLead(lead);
              }
            }}
          />
        </div>
      </>
    );
  }
}